import { API_PREFIX, PublicSiteConfig } from '@kcm/contracts'

/**
 * Runtime site configuration (PLAN.md §4.5).
 *
 * Nothing about the site may be baked into the bundle — Vite inlines `VITE_*`
 * at build time, so a domain in the build is a domain that needs a rebuild to
 * change. One immutable bundle serves every site; this is how it learns which
 * one it is.
 */
export async function fetchSiteConfig(
  signal?: AbortSignal,
): Promise<PublicSiteConfig> {
  const res = await fetch(`${API_PREFIX}/config/public`, {
    credentials: 'same-origin',
    ...(signal ? { signal } : {}),
  })
  if (!res.ok) throw new Error(`config request failed: ${res.status}`)
  // Validate rather than trust: a shape change should fail loudly here, not
  // as an undefined deep inside a component.
  return PublicSiteConfig.parse(await res.json())
}

/** Paint the site's brand tokens before first render. */
export function applyBrand(config: PublicSiteConfig): void {
  const root = document.documentElement
  root.style.setProperty('--brand-navy', config.brand.navy)
  root.style.setProperty('--brand-navy-deep', config.brand.navyDeep)
  root.style.setProperty('--brand-gold', config.brand.gold)
  root.style.setProperty('--brand-blue', config.brand.blue)
  root.lang = config.defaultLocale
}

/**
 * Injects the site's admin-authored head snippet — Meta Pixel, GA, GTM (§19).
 *
 * A `<script>` set via innerHTML never executes, so each is rebuilt as a real
 * element; other nodes (meta, link, noscript) are cloned across. Runs at most
 * once, so a re-render cannot double-fire a pixel. The markup is authored only
 * by an authenticated admin; when a CSP lands (§21, Phase 11) its `script-src`
 * has to allow these tags' origins.
 */
let headInjected = false
export function applyCustomHead(html: string | undefined): void {
  if (!html || headInjected) return
  headInjected = true
  const tpl = document.createElement('template')
  tpl.innerHTML = html
  for (const node of Array.from(tpl.content.childNodes)) {
    if (node instanceof HTMLScriptElement) {
      const script = document.createElement('script')
      for (const attr of Array.from(node.attributes)) script.setAttribute(attr.name, attr.value)
      script.text = node.textContent ?? ''
      document.head.appendChild(script)
    } else {
      document.head.appendChild(node.cloneNode(true))
    }
  }
}
